import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '@/features/auth';
import {
  clearEducationCart,
  readEducationCart,
  writeEducationCart,
} from './education-cart.storage';
import { EducationCartContext } from './education-cart.context';
import type {
  EducationCartContextValue,
  EducationCartItem,
} from './education-cart.type';

/**
 * 교육 장바구니. 회차를 여러 개 담아 한 번에 결제하기 위한 **클라이언트 상태**다.
 *
 * 서버에 카트를 두지 않는 이유: 담는 행위로 정원을 잡지 않기 때문이다. 정원은
 * 접수(결제 직전)에만 홀드된다 — 담아두기만 한 자리를 남이 못 사면 회차가 안 팔린다.
 * 그래서 결제 화면에서 잔여석이 모자라면 서버가 그때 거절한다.
 *
 * 교육 내부 라우트 이동과 로그인 왕복에서는 sessionStorage 로 복원한다. 일반 페이지로
 * 이탈하면 선택을 폐기해, 나중에 교육 화면으로 돌아왔을 때 이전 선택바가 남지 않게 한다.
 */
export function EducationCartProvider({ children }: { children: ReactNode }) {
  const { user, loading, unavailable } = useAuth();
  const [items, setItems] = useState<EducationCartItem[]>(
    () => readEducationCart().items,
  );

  /**
   * 저장된 선택의 소유자와 지금 로그인한 사람을 맞춘다.
   *
   * 로그아웃은 `window.location.replace` 하드 리다이렉트라 provider 의 언마운트 정리가
   * 아예 실행되지 않는다. sessionStorage 는 탭이 살아 있는 한 남으므로, 이 대조가 없으면
   * 같은 탭에서 다음에 로그인한 사람이 앞사람의 선택을 그대로 물려받는다.
   *
   * - 비로그인으로 담은 것(ownerId=null)은 **로그인한 사람이 이어받는다** — 접수하려다
   *   로그인 화면으로 튕긴 사람이 선택을 잃지 않아야 한다.
   * - 소유자가 다르면(로그아웃 포함) 버린다.
   * - 판정이 아직 안 된 동안(loading·서버 장애)에는 아무것도 하지 않는다.
   */
  useEffect(() => {
    if (loading || unavailable) return;
    const stored = readEducationCart();
    if (stored.ownerId === (user?.id ?? null)) return;

    if (stored.ownerId === null && user) {
      writeEducationCart(user.id, stored.items);
      return;
    }
    clearEducationCart();
    setItems([]);
  }, [loading, unavailable, user]);

  useEffect(
    () => () => {
      // StrictMode의 개발용 effect 재실행 시에는 아직 /education 이므로 지우지 않는다.
      // 비로그인 접수의 /auth 왕복은 선택 회차를 보존해야 한다.
      const nextPath = window.location.pathname;
      if (
        !nextPath.startsWith('/education') &&
        !nextPath.startsWith('/auth')
      ) {
        clearEducationCart();
      }
    },
    [],
  );

  // 담을 때의 로그인 사용자를 함께 새긴다 — 나중에 다른 계정이 물려받지 못하게 하는 근거다.
  const commit = useCallback(
    (next: EducationCartItem[]) => {
      setItems(next);
      writeEducationCart(user?.id ?? null, next);
    },
    [user?.id],
  );

  const value = useMemo<EducationCartContextValue>(() => {
    const has = (sessionId: string) =>
      items.some((i) => i.sessionId === sessionId);
    const remove = (sessionId: string) =>
      commit(items.filter((i) => i.sessionId !== sessionId));
    const add = (item: EducationCartItem) => {
      if (has(item.sessionId)) return;
      commit([...items, item]);
    };

    return {
      items,
      count: items.length,
      has,
      add,
      remove,
      toggle: (item) => (has(item.sessionId) ? remove(item.sessionId) : add(item)),
      clear: () => commit([]),
    };
  }, [items, commit]);

  return (
    <EducationCartContext.Provider value={value}>
      {children}
    </EducationCartContext.Provider>
  );
}
